import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import AppNavbar from './components/AppNavbar';
import './App.css';
import readerData from './reader_data.json';
import ArticleManager from './pages/ArticleManager';
import SubscriptionPage from './pages/SubscriptionPage';
import AuthPage from './pages/AuthPage';
import AdminPage from './pages/AdminPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ReadingComprehension from './pages/ReadingComprehension';
import VocabFlashCard from './components/VocabFlashCard';
import { getArticleDetail, getAllArticles } from './utils/articleManager';
import { getCurrentUser, isLoggedIn, recordReadArticle, getReadArticleCount, canReadArticle } from './utils/authManager';
import { getAudioUrl } from './config/cdn';

// ==================== 辅助函数 ====================
const getStoredProgress = (articleId) => {
  const stored = localStorage.getItem(`progress_${articleId}`);
  if (stored) return JSON.parse(stored);
  return {
    readingCompleted: false,
    translationCompleted: {},
    summaryCompleted: false,
    summaryScore: null,
    speakingCompleted: {},
    listeningCompleted: {},
    cteCompleted: false,
    lastUpdated: null,
    isShared: false
  };
};

const saveProgress = (articleId, progress) => {
  progress.lastUpdated = new Date().toISOString();
  localStorage.setItem(`progress_${articleId}`, JSON.stringify(progress));
};

// ==================== 文章列表页 ====================
function HomePage() {
  const [articles, setArticles] = useState([]);
  const [user, setUser] = useState(null);
  const [readCount, setReadCount] = useState(0);

  useEffect(() => {
    // 使用 articleManager 获取文章列表（会自动处理版本控制和静态数据）
    const allArticles = getAllArticles();
    setArticles(allArticles.filter(a => a.enabled));
    
    // 获取用户信息
    setUser(getCurrentUser());
    setReadCount(getReadArticleCount());
  }, []);

  const handleArticleClick = (articleId) => {
    if (!isLoggedIn()) {
      window.location.href = '/auth';
      return;
    }
    
    if (!canReadArticle(articleId)) {
      alert('您的免费试用已达上限（3篇文章），请订阅会员以继续学习！');
      window.location.href = '/subscription';
      return;
    }
    
    recordReadArticle(articleId);
    setReadCount(prev => prev + 1);
    window.location.href = `/article/${articleId}`;
  };

  return (
    <div className="main-container">
      <header className="app-header">
        <div className="header-left">
          <div className="logo">
            <span className="logo-icon">✈️</span>
            <div>
              <h1>智伴精读</h1>
              <p className="logo-subtitle">AEN 五维一体 · 外刊精读</p>
            </div>
          </div>
        </div>
        <AppNavbar currentUser={user} readCount={readCount} />
      </header>

      <div className="hero-banner">
        <div className="hero-content">
          <div className="hero-left">
            <h2>📚 外刊精读训练平台</h2>
            <p className="hero-tagline">让阅读更有深度 · 让英语更有温度</p>
            <p className="hero-desc">基于上海高考英语标准，精选外刊文章，五维一体精读训练体系</p>
            <a 
              href="https://aenedu.site" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hero-link"
            >
              🔗 了解智伴英航主营业务 →
            </a>
          </div>
          <div className="hero-right">
            <h3>📊 五维一体教学法</h3>
            <div className="five-dimensions">
              <div className="dimension-item">
                <span className="dimension-icon">📖</span>
                <span className="dimension-name">原文精读</span>
                <span className="dimension-desc">深度理解</span>
              </div>
              <div className="dimension-item">
                <span className="dimension-icon">🎧</span>
                <span className="dimension-name">听力精炼</span>
                <span className="dimension-desc">磨耳朵</span>
              </div>
              <div className="dimension-item">
                <span className="dimension-icon">🎙️</span>
                <span className="dimension-name">口语跟读</span>
                <span className="dimension-desc">练发音</span>
              </div>
              <div className="dimension-item">
                <span className="dimension-icon">✍️</span>
                <span className="dimension-name">翻译练习</span>
                <span className="dimension-desc">中英互译</span>
              </div>
              <div className="dimension-item">
                <span className="dimension-icon">📝</span>
                <span className="dimension-name">摘要写作</span>
                <span className="dimension-desc">输出表达</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section-header">
        <h2>📚 精选外刊</h2>
        <p>每周更新，覆盖时政、科技、文化等领域</p>
      </div>

      <div className="two-column-layout">
        {articles.map(article => {
          const progress = getStoredProgress(article.id);
          const readingDone = progress.readingCompleted;
          const translationCount = Object.values(progress.translationCompleted || {}).filter(v => v === true).length;
          const summaryDone = progress.summaryCompleted;
          const speakingCount = Object.values(progress.speakingCompleted || {}).filter(v => v === true).length;
          const listeningCount = Object.values(progress.listeningCompleted || {}).filter(v => v === true).length;
          const totalParagraphs = readerData.paragraphs.length;
          const overallProgress = ((readingDone?1:0) + (translationCount/totalParagraphs) + (summaryDone?1:0) + (speakingCount/totalParagraphs) + (listeningCount/totalParagraphs)) / 5 * 100;
          const hasRead = user?.readArticles?.includes(article.id);
          
          return (
            <div 
              key={article.id} 
              className={`article-card ${!isLoggedIn() ? 'article-card-locked' : ''}`}
              onClick={() => handleArticleClick(article.id)}
            >
              <div className="article-icon">{article.cover}</div>
              <h3 className="article-title">{article.title}</h3>
              <p className="article-source">{article.source} · {article.date}</p>
              {!isLoggedIn() && (
                <div className="lock-badge">🔒 请登录后阅读</div>
              )}
              {hasRead && (
                <div className="read-badge">✓ 已阅读</div>
              )}
              <div className="progress-bar-container">
                <div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} />
              </div>
              <div className="progress-stats">
                <span>📖 {readingDone ? "✓" : "○"}</span>
                <span>✍️ {translationCount}/{totalParagraphs}</span>
                <span>📝 {summaryDone ? "✓" : "○"}</span>
                <span>🎙️ {speakingCount}/{totalParagraphs}</span>
                <span>🎧 {listeningCount}/{totalParagraphs}</span>
              </div>
            </div>
          );
        })}
      </div>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>✈️ 智伴英航教育</h4>
            <p>专注于K12英语教育，提供全方位的学习解决方案</p>
          </div>
          <div className="footer-section">
            <h4>📞 联系方式</h4>
            <p>电话：187-8306-7878</p>
            <p>邮箱：aenedu.site@gmail.com</p>
            <p>地址：上海市 · 松江区 · 九亭镇</p>
          </div>
          <div className="footer-section">
            <h4>🔗 快速链接</h4>
            <Link to="/">首页</Link>
            <Link to="/subscription">订阅中心</Link>
            <Link to="/manager">文章管理</Link>
          </div>
        </div>
        <div className="footer-copyright">
          © 2026 智伴英航教育科技有限公司 版权所有
        </div>
      </footer>
    </div>
  );
}

// ==================== 精读页面 ====================
function ReadingPageContent({ article, articleId }) {
  // 所有Hooks必须在组件顶层
  const [progress, setProgress] = useState(() => getStoredProgress(articleId));
  const [showReport, setShowReport] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const [, setIsSharing] = useState(false);
  const [showConfirmShare, setShowConfirmShare] = useState(false);
  
  const [coins, setCoins] = useState(() => {
    const stored = localStorage.getItem(`coins_${articleId}`);
    return stored ? parseInt(stored) : 0;
  });
  const [showCoinToast, setShowCoinToast] = useState(false);
  const [lastEarned, setLastEarned] = useState(0);
  const [coinMessage, setCoinMessage] = useState('');

  const [, setUserTranslation] = useState('');
  const [showStandard, setShowStandard] = useState(false);
  const [selfRating, setSelfRating] = useState(null);
  const [selectedParaIndex, setSelectedParaIndex] = useState(0);

  const [, ] = useState('self');
  const [cteSelectedParaIndex, setCteSelectedParaIndex] = useState(0);
  const [cteUserTranslation, setCteUserTranslation] = useState('');
  const [showCteStandard, setShowCteStandard] = useState(false);
  const [, setCteSelfRating] = useState(null);
  const [aiCteFeedback, setAiCteFeedback] = useState('');
  const [isCteAnalyzing, setIsCteAnalyzing] = useState(false);

  const [userSummary, setUserSummary] = useState('');
  const [, ] = useState('self');
  const [showStandardSummary, setShowStandardSummary] = useState(false);
  const [aiSummaryFeedback, setAiSummaryFeedback] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [wordCount, setWordCount] = useState(0);

  const [speechParaIndex, setSpeechParaIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [recognizedText, setRecognizedText] = useState('');
  
  // 词汇闪卡状态
  const [showFlashCard, setShowFlashCard] = useState(false);
  const [speechScore, setSpeechScore] = useState(null);
  const [matchDetails, setMatchDetails] = useState(null);

  const [listenPlaybackRate, setListenPlaybackRate] = useState(1.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudioMode, setCurrentAudioMode] = useState('full'); // 'full' 或 'section'
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [gapWords, setGapWords] = useState([]);
  const [userAnswers, setUserAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);
  const [visibleTranslations, setVisibleTranslations] = useState({});
  const audioRef = useRef(null);
  
  // 切换译文显示状态
  const toggleTranslation = (index) => {
    setVisibleTranslations(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // 更新进度
  const updateProgress = (newProgress) => {
    setProgress(newProgress);
    saveProgress(articleId, newProgress);
  };

  // 添加金币
  const addCoins = (amount, reason) => {
    const newCoins = coins + amount;
    setCoins(newCoins);
    localStorage.setItem(`coins_${articleId}`, newCoins);
    setLastEarned(amount);
    setCoinMessage(reason);
    setShowCoinToast(true);
    setTimeout(() => setShowCoinToast(false), 2000);
    
    const records = JSON.parse(localStorage.getItem(`coin_records_${articleId}`) || '[]');
    records.push({ amount, reason, time: new Date().toISOString() });
    localStorage.setItem(`coin_records_${articleId}`, JSON.stringify(records.slice(-20)));
  };

  // 滚动到指定模块
  const scrollToModule = (moduleId) => {
    // 特殊处理词汇闪卡模块
    if (moduleId === 'flashcard-module') {
      setShowFlashCard(true);
      setMobileMenuOpen(false);
      return;
    }
    
    const element = document.getElementById(moduleId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  // 分享功能
  const isShareSupported = () => {
    return typeof navigator !== 'undefined' && navigator.share && typeof navigator.share === 'function';
  };

  const handleShare = async () => {
    if (progress.isShared) {
      alert('您已完成分享，每篇文章仅首次分享可获得金币奖励！');
      return;
    }

    const translationCount = Object.values(progress.translationCompleted || {}).filter(v => v === true).length;
    const speakingCount = Object.values(progress.speakingCompleted || {}).filter(v => v === true).length;
    const listeningCount = Object.values(progress.listeningCompleted || {}).filter(v => v === true).length;
    const totalParagraphs = article.paragraphs.length;
    
    const shareTitle = '智伴精读学习成果';
    const shareText = `我在智伴精读学习了「${article.title}」！\n📖 精读 ${progress.readingCompleted ? '✅' : '❌'}\n✍️ 翻译 ${translationCount}/${totalParagraphs}\n📝 摘要 ${progress.summaryCompleted ? '✅' : '❌'}\n🎙️ 口语 ${speakingCount}/${totalParagraphs}\n🎧 听力 ${listeningCount}/${totalParagraphs}\n💎 累计获得金币：${coins}`;
    const shareUrl = window.location.href;

    if (isShareSupported()) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        setIsSharing(true);
        setShowConfirmShare(true);
        alert('请确认您已完成分享，然后点击「我已分享」按钮领取20金币奖励');
      } catch (error) {
        fallbackShare(shareText, shareUrl);
      }
    } else {
      fallbackShare(shareText, shareUrl);
    }
  };

  const fallbackShare = (shareText, shareUrl) => {
    const fullText = `${shareText}\n\n分享链接：${shareUrl}`;
    navigator.clipboard.writeText(fullText).then(() => {
      setIsSharing(true);
      setShowConfirmShare(true);
      alert('学习成果已复制！请粘贴分享到微信朋友圈，完成后点击「我已分享」按钮领取20金币');
    }).catch(() => {
      alert('复制失败，请手动复制页面链接分享');
    });
  };

  const confirmShare = () => {
    if (progress.isShared) {
      alert('您已领取过分享奖励！');
      return;
    }
    addCoins(20, '分享学习成果');
    updateProgress({ ...progress, isShared: true });
    setIsSharing(false);
    setShowConfirmShare(false);
    alert('恭喜获得20金币！感谢您的分享！');
  };

  const cancelShareConfirm = () => {
    setIsSharing(false);
    setShowConfirmShare(false);
  };

  // 完成精读
  const markReadingComplete = () => {
    if (!progress.readingCompleted) {
      updateProgress({ ...progress, readingCompleted: true });
      addCoins(10, '完成精读');
    }
  };

  // 计算进度
  const totalParagraphs = article.paragraphs.length;
  const translationSelfRatedCount = Object.values(progress.translationSelfRated || {}).filter(v => v === true).length;
  const speakingDoneCount = Object.values(progress.speakingCompleted || {}).filter(v => v === true).length;
  const listeningDoneCount = Object.values(progress.listeningCompleted || {}).filter(v => v === true).length;
  const readingProgress = progress.readingCompleted ? 100 : 0;
  const translationProgress = (translationSelfRatedCount / totalParagraphs) * 100;
  const summaryProgress = progress.summaryCompleted ? 100 : 0;
  const speakingProgress = (speakingDoneCount / totalParagraphs) * 100;
  const listeningProgress = (listeningDoneCount / totalParagraphs) * 100;
  const overallProgress = (readingProgress + translationProgress + summaryProgress + speakingProgress + listeningProgress) / 5;

  // 翻译自查功能
  const currentPara = article.paragraphs[selectedParaIndex];
  
  const handleSelfRating = (rating) => {
    setSelfRating(rating);
    if (!progress.translationSelfRated?.[selectedParaIndex]) {
      const newSelfRated = { ...(progress.translationSelfRated || {}), [selectedParaIndex]: true };
      const newCompleted = { ...(progress.translationCompleted || {}), [selectedParaIndex]: true };
      updateProgress({ ...progress, translationCompleted: newCompleted, translationSelfRated: newSelfRated });
      addCoins(3, `第${selectedParaIndex + 1}段翻译自评完成`);
    }
  };

  // 汉译英练习功能
  const cteCurrentPara = article.paragraphs[cteSelectedParaIndex];
  
  const handleAnalyzeChineseToEnglish = async () => {
    if (!cteUserTranslation.trim()) {
      alert('请先输入你的英文翻译');
      return;
    }
    
    setIsCteAnalyzing(true);
    setAiCteFeedback('');
    
    try {
      const response = await fetch('https://open.bigmodel.cn/api/paas/v4/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_GLM_API_KEY}`
        },
        body: JSON.stringify({
          model: 'glm-4-flash',
          messages: [
            {
              role: 'system',
              content: `你是一位上海高考英语阅卷专家。请批阅学生的汉译英练习。

【评分标准】
总分10分 = 准确性(5分) + 语言表达(3分) + 翻译技巧(2分)

【输出格式】
━━━━━━━━━━━━━━━━━━━━
📊 汉译英评分

【总分】XX/10
【准确性】X/5 - 评语
【语言表达】X/3 - 评语
【翻译技巧】X/2 - 评语

【主要问题】
• 问题1
• 问题2

【优化译文】
${cteCurrentPara?.original.replace(/<[^>]*>/g, '') || ''}`
            },
            {
              role: 'user',
              content: `中文原文：${cteCurrentPara?.translation || ''}\n\n学生译文：${cteUserTranslation}\n\n标准译文参考：${cteCurrentPara?.original.replace(/<[^>]*>/g, '') || ''}`
            }
          ]
        })
      });
      
      const data = await response.json();
      if (data.choices && data.choices[0]) {
        setAiCteFeedback(data.choices[0].message.content);
        if (!progress.cteCompleted) {
          updateProgress({ ...progress, cteCompleted: true });
          addCoins(15, '完成汉译英练习');
        }
      } else {
        setAiCteFeedback('AI批阅失败，请稍后重试');
      }
    } catch (error) {
      setAiCteFeedback('网络错误，请检查API配置');
    } finally {
      setIsCteAnalyzing(false);
    }
  };

  // 摘要写作功能
  const handleSummaryChange = (e) => {
    const text = e.target.value;
    setUserSummary(text);
    const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
    setWordCount(words);
  };

  const handleAnalyzeSummary = async () => {
    if (!userSummary.trim()) {
      alert('请先输入你的英文摘要');
      return;
    }
    if (wordCount > 60) {
      alert(`当前词数：${wordCount}，摘要不能超过60词，请精简后再提交。`);
      return;
    }
    
    setIsAnalyzing(true);
    setAiSummaryFeedback('');
    
    try {
      const response = await fetch('https://open.bigmodel.cn/api/paas/v4/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_GLM_API_KEY}`
        },
        body: JSON.stringify({
          model: 'glm-4-flash',
          messages: [
            {
              role: 'system',
              content: `你是一位上海高考英语阅卷专家。请严格按照上海高考Summary评分标准批阅学生的英文摘要。

【评分标准】
总分10分 = 内容(4分) + 语言(3分) + 结构(3分)

【输出格式】
━━━━━━━━━━━━━━━━━━━━
📊 上海高考Summary评分

【总分】XX/10
【内容】X/4 - 评语
【语言】X/3 - 评语
【结构】X/3 - 评语
【词数统计】XX词

【主要问题】
• 问题1
• 问题2

【修改示范】
修改建议：...

【得分建议】
当前等级：A/B/C/D（A:8-10, B:6-7, C:4-5, D:0-3）
━━━━━━━━━━━━━━━━━━━━

【标准摘要参考】
${article.summary}

请严格按照上述格式输出。`
            },
            {
              role: 'user',
              content: `学生摘要（${wordCount}词）：\n${userSummary}`
            }
          ]
        })
      });
      
      const data = await response.json();
      if (data.choices && data.choices[0]) {
        const feedback = data.choices[0].message.content;
        setAiSummaryFeedback(feedback);
        const scoreMatch = feedback.match(/【总分】(\d+)\/10/);
        if (scoreMatch && !progress.summaryCompleted) {
          updateProgress({ ...progress, summaryCompleted: true, summaryScore: parseInt(scoreMatch[1]) });
          addCoins(20, '完成摘要写作');
        }
      } else {
        setAiSummaryFeedback('AI批阅失败，请稍后重试');
      }
    } catch (error) {
      setAiSummaryFeedback('网络错误，请检查API配置');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 跟读口语功能
  const currentSpeechPara = article.paragraphs[speechParaIndex];
  const originalText = currentSpeechPara.original.replace(/<[^>]*>/g, '');
  
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('您的浏览器不支持语音识别，请使用Chrome、Edge或Safari浏览器');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    
    recognition.onstart = () => {
      setIsListening(true);
      setRecognizedText('');
      setSpeechScore(null);
      setMatchDetails(null);
    };
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setRecognizedText(transcript);
      calculateMatch(originalText, transcript);
      
      if (!progress.speakingCompleted[speechParaIndex]) {
        const newCompleted = { ...progress.speakingCompleted, [speechParaIndex]: true };
        updateProgress({ ...progress, speakingCompleted: newCompleted });
        addCoins(5, `完成第${speechParaIndex + 1}段跟读`);
      }
    };
    
    recognition.onerror = (event) => {
      alert('识别失败：' + event.error);
      setIsListening(false);
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognition.start();
  };
  
  const calculateMatch = (original, recognized) => {
    const cleanText = (text) => text.toLowerCase().replace(/[^\w\s]/g, '');
    const originalWords = cleanText(original).split(/\s+/);
    const recognizedWords = cleanText(recognized).split(/\s+/);
    
    let matchCount = 0;
    recognizedWords.forEach(word => {
      if (originalWords.includes(word)) matchCount++;
    });
    
    const matchRate = recognizedWords.length > 0 ? (matchCount / recognizedWords.length) * 100 : 0;
    const lengthRatio = Math.min(recognizedWords.length / originalWords.length, 1);
    const finalScore = (matchRate * 0.7 + lengthRatio * 30) * 0.8;
    
    setSpeechScore(Math.min(Math.round(finalScore), 100));
    setMatchDetails({
      originalLength: originalWords.length,
      recognizedLength: recognizedWords.length,
      matchRate: Math.round(matchRate)
    });
  };
  
  const speakParagraph = () => {
    if (!originalText) return;
    
    // 检查是否有自定义音频文件和段落时间戳
    const currentParagraph = article.paragraphs[speechParaIndex];
    if (article.audioFile && currentParagraph && currentParagraph.audioStart !== undefined && currentParagraph.audioEnd !== undefined) {
      // 使用自定义音频播放段落
      window.speechSynthesis.cancel();
      if (audioRef.current) {
        audioRef.current.src = getAudioUrl(article.audioFile);
        audioRef.current.currentTime = currentParagraph.audioStart;
        audioRef.current.playbackRate = 1.0; // 跟读使用正常语速
        
        // 设置监听，在段落结束时停止播放
        const handleTimeUpdate = () => {
          if (audioRef.current && audioRef.current.currentTime >= currentParagraph.audioEnd) {
            audioRef.current.pause();
            audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
          }
        };
        
        audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.play();
      }
    } else {
      // 使用浏览器自带的语音合成
      const utterance = new SpeechSynthesisUtterance(originalText);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    }
  };

  // 听力练习功能
  const [fullArticleWords, setFullArticleWords] = useState([]);
  const [sectionStartIndex, setSectionStartIndex] = useState(0);
  const [sectionEndIndex, setSectionEndIndex] = useState(0);
  
  useEffect(() => {
    // 计算每段文章的单词范围
    let wordIndex = 0;
    const paragraphs = article.paragraphs || [];
    const newSectionStart = [];
    const newSectionEnd = [];
    
    paragraphs.forEach(para => {
      const cleanText = para.original.replace(/<[^>]*>/g, '');
      const words = cleanText.split(/\s+/).filter(w => w.length > 0);
      newSectionStart.push(wordIndex);
      newSectionEnd.push(wordIndex + words.length - 1);
      wordIndex += words.length;
    });
    
    setSectionStartIndex(newSectionStart);
    setSectionEndIndex(newSectionEnd);
    
    const text = paragraphs.map(p => p.original.replace(/<[^>]*>/g, '')).join(' ');
    setFullArticleWords(text.split(/\s+/).filter(w => w.length > 0));
  }, [article]);

  const generateGaps = useCallback(() => {
    if (fullArticleWords.length === 0) return;
    
    let wordsToProcess = fullArticleWords;
    let startIdx = 0;
    
    // 分段模式：只对当前段落的单词进行挖空
    if (currentAudioMode === 'section' && sectionStartIndex[currentSectionIndex] !== undefined) {
      startIdx = sectionStartIndex[currentSectionIndex];
      const endIdx = sectionEndIndex[currentSectionIndex] + 1;
      wordsToProcess = fullArticleWords.slice(startIdx, endIdx);
    }
    
    const gapCount = Math.min(Math.max(Math.floor(wordsToProcess.length * 0.2), 3), 10);
    const indices = new Set();
    while (indices.size < gapCount) {
      indices.add(Math.floor(Math.random() * wordsToProcess.length));
    }
    const gapList = Array.from(indices).sort((a, b) => a - b).map(idx => ({
      index: startIdx + idx,
      word: wordsToProcess[idx]
    }));
    setGapWords(gapList);
    setUserAnswers({});
    setShowAnswers(false);
  }, [fullArticleWords, currentAudioMode, currentSectionIndex, sectionStartIndex, sectionEndIndex]);

  useEffect(() => {
    if (fullArticleWords.length > 0) {
      generateGaps();
    }
  }, [fullArticleWords, articleId, generateGaps, currentAudioMode, currentSectionIndex]);
  
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };
  
  const updateAnswer = (index, value) => {
    setUserAnswers(prev => ({ ...prev, [index]: value }));
  };
  
  const checkAnswers = () => {
    setShowAnswers(true);
    if (!progress.listeningCompleted[0]) {
      const newCompleted = { ...progress.listeningCompleted, '0': true };
      updateProgress({ ...progress, listeningCompleted: newCompleted });
      addCoins(15, '完成听力填空');
    }
  };
  
  const correctCount = gapWords.filter(gap => 
    userAnswers[gap.index]?.trim().toLowerCase() === gap.word.toLowerCase()
  ).length;
  
  const buildDisplayText = () => {
    const result = [];
    
    // 确定显示范围
    let startIdx = 0;
    let endIdx = fullArticleWords.length;
    
    // 分段模式：只显示当前段落
    if (currentAudioMode === 'section' && sectionStartIndex[currentSectionIndex] !== undefined) {
      startIdx = sectionStartIndex[currentSectionIndex];
      endIdx = sectionEndIndex[currentSectionIndex] + 1;
    }
    
    let lastIndex = startIdx;
    gapWords.forEach((gap, gapIdx) => {
      // 只处理当前段落范围内的单词
      if (gap.index < startIdx || gap.index > endIdx) return;
      
      for (let i = lastIndex; i < gap.index; i++) {
        result.push(fullArticleWords[i] + ' ');
      }
      const userAnswer = userAnswers[gap.index];
      const isCorrect = showAnswers && userAnswer?.trim().toLowerCase() === gap.word.toLowerCase();
      result.push(
        React.createElement('span', {
          key: `gap-${gap.index}`,
          style: {
            display: 'inline-block',
            minWidth: '70px',
            margin: '0 4px',
            padding: '2px 8px',
            backgroundColor: showAnswers ? (isCorrect ? '#c8e6c9' : '#ffcdd2') : '#fff3e0',
            borderBottom: '2px solid var(--accent)',
            borderRadius: '4px',
            fontWeight: 'bold',
            fontSize: '13px'
          }
        }, showAnswers ? (isCorrect ? gap.word : `❌ ${gap.word}`) : `(${gapIdx + 1}) ______`)
      );
      lastIndex = gap.index + 1;
    });
    for (let i = lastIndex; i < endIdx; i++) {
      result.push(fullArticleWords[i] + ' ');
    }
    return result;
  };

  // 导航菜单项配置
  const navItems = [
    { id: 'report-module', label: '五维学习报告', icon: '📊' },
    { id: 'reading-module', label: '原文精读', icon: '📖' },
    { id: 'flashcard-module', label: '词汇闪卡', icon: '🎴' },
    { id: 'listening-module', label: '听力精练', icon: '🎧' },
    { id: 'speaking-module', label: '跟读口语', icon: '🎙️' },
    { id: 'translate-module', label: '英译汉翻译', icon: '✍️' },
    { id: 'cte-module', label: '汉译英翻译', icon: '🌐' },
    { id: 'summary-module', label: '摘要写作', icon: '📝' },
    { id: 'comprehension-module', label: '阅读理解', icon: '🧩' },
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* 金币提示浮层 */}
      {showCoinToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: 'var(--accent)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '30px',
          zIndex: 2000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          animation: 'fadeIn 0.3s ease'
        }}>
          ✨ +{lastEarned} 金币 ({coinMessage})
        </div>
      )}

      {/* 左侧导航栏 */}
      <div className="side-nav">
        <div className="side-nav-header">
          <div className="logo">
            <span className="logo-icon">✈️</span>
            <div>
              <h3>智伴精读</h3>
              <p className="logo-subtitle">五维一体 · 外刊精读</p>
            </div>
          </div>
        </div>
        
        <div className="side-nav-back-btn">
          <Link to="/" className="btn-back-home">
            <span>🏠</span>
            返回首页
          </Link>
        </div>
        
        <div className="coins-display">
          <span>💎 我的金币</span>
          <span className="coins-count">{coins}</span>
        </div>
        
        <div className="side-nav-menu">
          {navItems.map(item => (
            <div key={item.id} className="side-nav-item" onClick={() => scrollToModule(item.id)}>
              <span>{item.icon}</span>
              {item.label}
            </div>
          ))}
          <div className="side-nav-item" onClick={handleShare}>
            <span>📤</span>
            分享得20金币 {progress.isShared && <span>✅</span>}
          </div>
          
          {showConfirmShare && !progress.isShared && (
            <div className="share-confirm-panel">
              <div>📤 已完成分享？</div>
              <div>
                <button onClick={confirmShare} className="btn btn-success btn-sm">是，我已分享 ✓</button>
                <button onClick={cancelShareConfirm} className="btn btn-outline btn-sm">取消 ✗</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 移动端汉堡菜单 */}
      <div className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>☰</div>
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="logo">
                <span className="logo-icon">✈️</span>
                <span>智伴精读</span>
              </div>
            </div>
            <div className="coins-display">
              <span>💎 我的金币</span>
              <span className="coins-count">{coins}</span>
            </div>
            {navItems.map(item => (
              <div key={item.id} className="mobile-menu-item" onClick={() => scrollToModule(item.id)}>
                <span>{item.icon}</span>
                {item.label}
              </div>
            ))}
            <div className="mobile-menu-item" onClick={handleShare}>
              <span>📤</span>
              分享得20金币 {progress.isShared && <span style={{ color: 'green' }}>✓</span>}
            </div>
            {showConfirmShare && !progress.isShared && (
              <div className="share-confirm-panel">
                <div>📤 已完成分享？</div>
                <div>
                  <button onClick={confirmShare} className="btn btn-success btn-sm">是，我已分享 ✓</button>
                  <button onClick={cancelShareConfirm} className="btn btn-outline btn-sm">取消 ✗</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 右侧内容区 */}
      <div className="main-content">
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header">
            <div className="card-title"><span>📖</span> {article.title}</div>
          </div>
          <p style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>来源：{article.source}</p>
        </div>

        {/* 报告面板 */}
        <div id="report-module">
          <div className="report-panel">
            <div className="report-header" onClick={() => setShowReport(!showReport)}>
              <span>📊 五维学习报告</span>
              <span>{showReport ? "▲" : "▼"}</span>
            </div>
            {showReport && (
              <div className="report-content fade-in">
                <div style={{ marginBottom: '16px' }}>
                  <div className="report-dimension-header"><span>🎯 总体学习进度</span><span>{Math.round(overallProgress)}%</span></div>
                  <div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${overallProgress}%` }} /></div>
                </div>
                <div className="report-dimension"><div className="report-dimension-header"><span>📖 原文精读</span><span>{progress.readingCompleted ? "✅ 已完成" : "⭕ 未完成"}</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${readingProgress}%` }} /></div></div>
                <div className="report-dimension"><div className="report-dimension-header"><span>🎧 听力练习</span><span>{listeningDoneCount}/{totalParagraphs} 段</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${listeningProgress}%` }} /></div></div>
                <div className="report-dimension"><div className="report-dimension-header"><span>🎙️ 跟读口语</span><span>{speakingDoneCount}/{totalParagraphs} 段</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${speakingProgress}%` }} /></div></div>
                <div className="report-dimension"><div className="report-dimension-header"><span>✍️ 英译汉翻译</span><span>{translationSelfRatedCount}/{totalParagraphs} 段</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${translationProgress}%` }} /></div><p style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>完成自我评价后自动标记</p></div>
                <div className="report-dimension"><div className="report-dimension-header"><span>🌐 汉译英翻译</span><span>{progress.cteCompleted ? "✅ 已完成" : "⭕ 未完成"}</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: progress.cteCompleted ? 100 : 0 }} /></div></div>
                <div className="report-dimension"><div className="report-dimension-header"><span>📝 摘要写作</span><span>{progress.summaryCompleted ? `已完成 (${progress.summaryScore || '?'}/10分)` : "⭕未完成"}</span></div><div className="progress-bar-container" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}><div className="progress-bar-fill" style={{ width: `${summaryProgress}%` }} /></div></div>
                
                {overallProgress === 100 && <div className="complete-badge">🎉 恭喜！你已完成这篇文章的五维学习！ 🎉</div>}
              </div>
            )}
          </div>
        </div>

        {/* 原文精读模块 */}
        <div id="reading-module">
          <h3 className="mb-3" style={{ color: 'var(--primary-dark)', marginTop: '20px' }}>📄 原文精读</h3>
          {article.paragraphs.map((para, idx) => (
            <div key={idx} className="paragraph-card">
              <div className="original-text" dangerouslySetInnerHTML={{ __html: para.original }} />
              <button 
                className="btn btn-outline btn-sm" 
                onClick={() => toggleTranslation(idx)}
                style={{ marginBottom: '12px' }}
              >
                {visibleTranslations[idx] ? '🙈 隐藏译文' : '👁️ 显示译文'}
              </button>
              {visibleTranslations[idx] && (
                <div className="translation-text">{para.translation}</div>
              )}
              <details className="vocab-details">
                <summary className="vocab-summary">📖 本段词汇 ({para.vocab.length}个) </summary>
                <ul className="vocab-list">{para.vocab.map((v, i) => (<li key={i} className="vocab-item"><span className="vocab-word">{v.word}</span><span className="vocab-level">{v.level}</span><br /><span style={{ fontSize: '12px', color: 'var(--gray)' }}>{v.meaning_cn} · {v.meaning_en}</span></li>))}</ul>
              </details>
            </div>
          ))}
          <div style={{ marginTop: '20px', textAlign: 'center' }}>
            <button className="btn btn-accent" onClick={() => setShowFlashCard(true)}>
              🎴 词汇闪卡学习
            </button>
          </div>
        </div>
        
        <div style={{ height: '1px' }} ref={(el) => { if (el) { const observer = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) markReadingComplete(); }); observer.observe(el); } }} />

        {/* 听力练习模块 */}
        <div id="listening-module" className="exercise-module exercise-listening">
          <div className="exercise-title">🎧 听力精练</div>
          <div className="exercise-subtitle">选择播放模式 → 调整语速 → 播放音频 → 完成填空练习</div>
          
          {/* 播放模式选择 */}
          <div style={{ marginBottom: '15px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold' }}>播放模式：</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                className={`btn btn-sm ${currentAudioMode === 'full' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => { setCurrentAudioMode('full'); stopAudio(); }}
              >
                📄 整篇播放
              </button>
              <button 
                className={`btn btn-sm ${currentAudioMode === 'section' ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => { setCurrentAudioMode('section'); stopAudio(); }}
              >
                📑 分段播放
              </button>
            </div>
          </div>
          
          {/* 语速控制 */}
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'bold' }}>播放语速：</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[0.8, 1.0, 1.2].map(rate => (
                <button 
                  key={rate} 
                  className={`btn btn-sm ${listenPlaybackRate === rate ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => { 
                    setListenPlaybackRate(rate); 
                    if (audioRef.current && !audioRef.current.paused) { 
                      audioRef.current.playbackRate = rate; 
                    } 
                  }}
                >
                  {rate}x
                </button>
              ))}
            </div>
          </div>
          
          {/* 分段选择器（分段模式时显示） */}
          {currentAudioMode === 'section' && (
            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 'bold' }}>选择段落：</span>
              <select 
                value={currentSectionIndex} 
                onChange={(e) => { setCurrentSectionIndex(Number(e.target.value)); stopAudio(); }}
                style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-lighter)' }}
              >
                {article.paragraphs.map((_, idx) => (
                  <option key={idx} value={idx}>段落 {idx + 1}</option>
                ))}
              </select>
              <span style={{ fontSize: '12px', color: 'var(--gray)' }}>
                当前：段落 {currentSectionIndex + 1} / {article.paragraphs.length}
              </span>
            </div>
          )}
          
          {/* 播放控制按钮 */}
          <div style={{ marginBottom: '15px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <audio 
              ref={audioRef} 
              onPlay={() => setIsPlaying(true)} 
              onPause={() => setIsPlaying(false)} 
              onEnded={() => setIsPlaying(false)} 
              style={{ display: 'none' }} 
            />
            {currentAudioMode === 'full' ? (
              <button 
                className="btn btn-success" 
                onClick={() => { 
                  if (audioRef.current && article.audioFile) { 
                    audioRef.current.src = getAudioUrl(article.audioFile);
                    audioRef.current.playbackRate = listenPlaybackRate; 
                    audioRef.current.play(); 
                  } else if (audioRef.current) {
                    audioRef.current.src = `/audio/full_article.m4a`;
                    audioRef.current.playbackRate = listenPlaybackRate; 
                    audioRef.current.play(); 
                  }
                }} 
                disabled={isPlaying}
              >
                🔊 {isPlaying ? '播放中...' : '播放全文音频'}
              </button>
            ) : (
              <button 
                className="btn btn-success" 
                onClick={() => { 
                  if (audioRef.current) { 
                    const currentParagraph = article.paragraphs[currentSectionIndex];
                    // 如果有自定义音频和时间戳，使用段落音频
                    if (article.audioFile && currentParagraph.audioStart !== undefined && currentParagraph.audioEnd !== undefined) {
                      audioRef.current.src = getAudioUrl(article.audioFile);
                      audioRef.current.currentTime = currentParagraph.audioStart;
                      audioRef.current.playbackRate = listenPlaybackRate;
                      const handleTimeUpdate = () => {
                        if (audioRef.current && audioRef.current.currentTime >= currentParagraph.audioEnd) {
                          audioRef.current.pause();
                          audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
                        }
                      };
                      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
                      audioRef.current.play();
                    } else {
                      // 回退到默认的段落音频文件
                      audioRef.current.src = `/audio/section_${currentSectionIndex + 1}.m4a`;
                      audioRef.current.playbackRate = listenPlaybackRate; 
                      audioRef.current.play(); 
                    }
                  } 
                }} 
                disabled={isPlaying}
              >
                🔊 {isPlaying ? '播放中...' : `播放段落 ${currentSectionIndex + 1}`}
              </button>
            )}
            <button className="btn btn-outline" onClick={stopAudio}>⏹️ 停止</button>
            <button className="btn btn-accent" onClick={generateGaps}>🔄 重新挖空</button>
          </div>
          
          {fullArticleWords.length > 0 && (
            <div style={{ backgroundColor: 'var(--bg-light)', padding: '16px', borderRadius: '8px', marginBottom: '15px', lineHeight: '1.8', fontSize: '14px', maxHeight: '300px', overflowY: 'auto' }}>
              <strong>📝 听力填空练习：</strong>
              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                {buildDisplayText()}
              </div>
            </div>
          )}
          {!showAnswers && gapWords.length > 0 && (
            <div style={{ backgroundColor: '#fff8e1', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
              <strong>✏️ 填写答案：</strong>
              <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '250px', overflowY: 'auto' }}>
                {gapWords.map((gap, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ minWidth: '30px', fontWeight: 'bold' }}>{idx + 1}.</span>
                    <input 
                      type="text" 
                      value={userAnswers[gap.index] || ''} 
                      onChange={(e) => updateAnswer(gap.index, e.target.value)} 
                      placeholder={`原文中的第 ${gap.index + 1} 个词`} 
                      style={{ flex: 1, padding: '8px 12px', fontSize: '14px', border: '1px solid var(--gray-lighter)', borderRadius: '6px', fontFamily: 'monospace' }} 
                    />
                    <span style={{ fontSize: '12px', color: 'var(--gray)' }}>位置：第 {gap.index + 1} 词</span>
                  </div>
                ))}
              </div>
              <button className="btn btn-primary" onClick={checkAnswers} style={{ marginTop: '15px' }}>提交批改</button>
            </div>
          )}
          {showAnswers && (
            <div style={{ backgroundColor: '#e8f5e9', padding: '15px', borderRadius: '8px' }}>
              <strong>📊 批改结果：</strong>
              <p style={{ marginTop: '8px', fontSize: '18px', fontWeight: 'bold', color: 'var(--success)' }}>正确：{correctCount} / {gapWords.length}</p>
              <p style={{ fontSize: '13px', color: 'var(--gray)' }}>{correctCount === gapWords.length ? '🎉 完美！听力很棒！' : '💪 再听一遍，注意正确拼写'}</p>
              <button className="btn btn-outline" onClick={() => { setShowAnswers(false); generateGaps(); }} style={{ marginTop: '10px' }}>继续练习</button>
            </div>
          )}
          <p style={{ fontSize: '12px', color: 'var(--gray-light)', marginTop: '15px', textAlign: 'center' }}>
            💡 提示：先听音频，再尝试填空。点击「重新挖空」可生成新的填空题。语速可实时调整。
          </p>
        </div>
        
        {/* 跟读口语模块 */}
        <div id="speaking-module" className="exercise-module exercise-speaking">
          <div className="exercise-title">🎙️ 跟读口语练习</div>
          <div className="exercise-subtitle">选择段落 → 聆听原音 → 点击麦克风跟读 → 获取匹配评分</div>
          <div style={{ marginBottom: '15px' }}><label style={{ fontWeight: 'bold', marginRight: '10px' }}>选择跟读段落：</label><select value={speechParaIndex} onChange={(e) => { setSpeechParaIndex(Number(e.target.value)); setRecognizedText(''); setSpeechScore(null); setMatchDetails(null); }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-lighter)' }}>{article.paragraphs.map((_, idx) => (<option key={idx} value={idx}>段落 {idx + 1}</option>))}</select></div>
          <div style={{ backgroundColor: 'var(--bg-light)', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontFamily: 'Georgia, serif', lineHeight: '1.6' }}><strong>📖 跟读文本：</strong><p style={{ marginTop: '8px' }}>{originalText}</p></div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}><button className="btn btn-success" onClick={speakParagraph}>🔊 聆听原音</button><button className="btn btn-primary" onClick={startListening} disabled={isListening} style={{ opacity: isListening ? 0.6 : 1 }}>{isListening ? "🎤 聆听中..." : "🎙️ 开始跟读"}</button></div>
          {recognizedText && (<div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'var(--bg-light)', borderRadius: '8px' }}><strong>🗣️ 你的朗读：</strong><p style={{ marginTop: '8px' }}>{recognizedText}</p></div>)}
          {speechScore !== null && (<div style={{ marginTop: '15px', padding: '15px', backgroundColor: speechScore >= 80 ? '#e8f5e9' : speechScore >= 60 ? '#fff8e1' : '#ffebee', borderRadius: '8px' }}><div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><strong>🎯 匹配度评分</strong><span style={{ fontSize: '2rem', fontWeight: 'bold', color: speechScore >= 80 ? 'var(--success)' : speechScore >= 60 ? 'var(--accent)' : 'var(--danger)' }}>{speechScore}%</span></div>{matchDetails && (<div style={{ marginTop: '10px', fontSize: '0.85rem', color: 'var(--gray)' }}><p>原文词数：{matchDetails.originalLength} | 识别词数：{matchDetails.recognizedLength} | 匹配率：{matchDetails.matchRate}%</p></div>)}</div>)}
        </div>
        
        {/* 英译汉翻译模块 */}
        <div id="translate-module" className="exercise-module exercise-translate">
          <div className="exercise-title">✍️ 英译汉翻译练习</div>
          <div className="exercise-subtitle">选择段落 → 参考标准翻译 → 进行自我评价</div>
          <div style={{ marginBottom: '15px' }}><label style={{ fontWeight: 'bold', marginRight: '10px' }}>选择段落：</label><select value={selectedParaIndex} onChange={(e) => { setSelectedParaIndex(Number(e.target.value)); setUserTranslation(''); setShowStandard(false); setSelfRating(null); }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-lighter)' }}>{article.paragraphs.map((_, idx) => (<option key={idx} value={idx}>段落 {idx + 1}</option>))}</select></div>
          <div style={{ backgroundColor: 'var(--bg-light)', padding: '16px', borderRadius: '8px', marginBottom: '15px', fontFamily: 'Georgia, serif', lineHeight: '1.6' }}><strong>📖 英文原文：</strong><p style={{ marginTop: '8px' }}>{currentPara.original.replace(/<[^>]*>/g, '')}</p></div>
          <div style={{ marginBottom: '15px' }}><button className="btn btn-primary" onClick={() => setShowStandard(!showStandard)}>{showStandard ? '隐藏标准翻译' : '显示标准翻译'}</button></div>
          {showStandard && (<div style={{ backgroundColor: '#e3f2fd', padding: '16px', borderRadius: '8px', marginBottom: '15px' }}><strong>📝 标准翻译：</strong><p style={{ marginTop: '8px', fontSize: '0.95rem', lineHeight: '1.6' }}>{currentPara.translation}</p></div>)}
          {!progress.translationCompleted[selectedParaIndex] && (<div style={{ backgroundColor: '#fff8e1', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}><strong>📋 自我评价：</strong><div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}><button className={`btn ${selfRating === 'good' ? 'btn-success' : 'btn-outline'}`} onClick={() => handleSelfRating('good')}>✅ 掌握良好</button><button className={`btn ${selfRating === 'medium' ? 'btn-accent' : 'btn-outline'}`} onClick={() => handleSelfRating('medium')}>⚡ 需要加强</button><button className={`btn ${selfRating === 'bad' ? 'btn-danger' : 'btn-outline'}`} onClick={() => handleSelfRating('bad')}>❌ 需重点复习</button></div></div>)}
          {progress.translationCompleted[selectedParaIndex] && (<div style={{ backgroundColor: '#e8f5e9', padding: '15px', borderRadius: '8px' }}><strong>✅ 已完成此段翻译</strong><p style={{ marginTop: '8px', fontSize: '0.85rem', color: 'var(--gray)' }}>点击其他段落继续练习</p></div>)}
        </div>
        
        {/* 汉译英翻译模块 */}
        <div id="cte-module" className="exercise-module">
          <div className="exercise-title">🌐 汉译英翻译练习</div>
          <div className="exercise-subtitle">选择段落 → 输入英文翻译 → 提交分析</div>
          <div style={{ marginBottom: '15px' }}><label style={{ fontWeight: 'bold', marginRight: '10px' }}>选择段落：</label><select value={cteSelectedParaIndex} onChange={(e) => { setCteSelectedParaIndex(Number(e.target.value)); setCteUserTranslation(''); setShowCteStandard(false); setCteSelfRating(null); setAiCteFeedback(''); }} style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid var(--gray-lighter)' }}>{article.paragraphs.map((_, idx) => (<option key={idx} value={idx}>段落 {idx + 1}</option>))}</select></div>
          <div style={{ backgroundColor: '#e3f2fd', padding: '16px', borderRadius: '8px', marginBottom: '15px' }}><strong>📝 中文原文：</strong><p style={{ marginTop: '8px', fontSize: '0.95rem', lineHeight: '1.6' }}>{cteCurrentPara.translation}</p></div>
          <textarea value={cteUserTranslation} onChange={(e) => setCteUserTranslation(e.target.value)} placeholder="请输入你的英文翻译..." rows={4} style={{ width: '100%', padding: '12px', border: '1px solid var(--gray-lighter)', borderRadius: '8px', fontSize: '14px', fontFamily: 'Georgia, serif' }} />
          <div style={{ marginTop: '15px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}><button className="btn btn-outline" onClick={() => setShowCteStandard(!showCteStandard)}>{showCteStandard ? '隐藏标准译文' : '参考标准译文'}</button><button className="btn btn-primary" onClick={handleAnalyzeChineseToEnglish} disabled={isCteAnalyzing}>{isCteAnalyzing ? '分析中...' : '🔍 AI分析翻译'}</button></div>
          {showCteStandard && (<div style={{ marginTop: '15px', backgroundColor: '#e8f5e9', padding: '16px', borderRadius: '8px' }}><strong>📖 标准译文：</strong><p style={{ marginTop: '8px', fontFamily: 'Georgia, serif', lineHeight: '1.6' }}>{cteCurrentPara.original.replace(/<[^>]*>/g, '')}</p></div>)}
          {aiCteFeedback && (<div style={{ marginTop: '15px', backgroundColor: 'var(--bg-light)', padding: '16px', borderRadius: '8px', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{aiCteFeedback}</div>)}
        </div>
        
        {/* 摘要写作模块 */}
        <div id="summary-module" className="exercise-module exercise-summary">
          <div className="exercise-title">📝 摘要写作练习</div>
          <div className="exercise-subtitle">阅读文章 → 撰写英文摘要（不超过60词）→ 获取AI评分</div>
          <textarea value={userSummary} onChange={handleSummaryChange} placeholder="请输入英文摘要（不超过60词）..." rows={6} style={{ width: '100%', padding: '12px', border: '1px solid var(--gray-lighter)', borderRadius: '8px', fontSize: '14px', fontFamily: 'Georgia, serif' }} />
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ color: wordCount > 60 ? 'var(--danger)' : 'var(--gray)' }}>词数：{wordCount}/60</span><button className="btn btn-outline" onClick={() => setShowStandardSummary(!showStandardSummary)}>{showStandardSummary ? '隐藏标准摘要' : '参考标准摘要'}</button></div>
          <div style={{ marginTop: '15px' }}><button className="btn btn-primary" onClick={handleAnalyzeSummary} disabled={isAnalyzing}>{isAnalyzing ? '评分中...' : '📊 AI评分'}</button></div>
          {showStandardSummary && (<div style={{ marginTop: '15px', backgroundColor: '#e8f5e9', padding: '16px', borderRadius: '8px' }}><strong>📖 标准摘要：</strong><p style={{ marginTop: '8px', fontFamily: 'Georgia, serif', lineHeight: '1.6', fontSize: '0.95rem' }}>{article.summary}</p></div>)}
          {aiSummaryFeedback && (<div style={{ marginTop: '15px', backgroundColor: 'var(--bg-light)', padding: '16px', borderRadius: '8px', whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{aiSummaryFeedback}</div>)}
        </div>
        
        {/* 阅读理解模块 */}
        <div id="comprehension-module" className="exercise-module exercise-comprehension">
          <div className="exercise-title">🧩 阅读理解练习</div>
          <div className="exercise-subtitle">完成阅读理解题目，检验阅读效果</div>
          {article.questions && article.questions.length > 0 ? (
            <div>
              <p style={{ color: 'var(--gray)', marginBottom: '16px' }}>共 {article.questions.length} 道题目，涵盖细节理解、词义猜测、推理判断等题型</p>
              <Link to={`/article/${articleId}/comprehension`} className="btn btn-primary">
                🚀 开始答题
              </Link>
            </div>
          ) : (
            <div style={{ backgroundColor: 'var(--bg-light)', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
              <p style={{ color: 'var(--gray)' }}>该文章暂无阅读理解题目</p>
              <p style={{ color: 'var(--gray-light)', fontSize: '0.9rem', marginTop: '8px' }}>管理员可以在文章管理页面添加题目</p>
            </div>
          )}
        </div>
      </div>
      
      {/* 词汇闪卡组件 */}
      {showFlashCard && (
        <VocabFlashCard 
          article={article} 
          onClose={() => setShowFlashCard(false)} 
        />
      )}
    </div>
  );
}

// 包装组件处理加载状态
function ReadingPage({ match }) {
  const articleId = parseInt(window.location.pathname.split('/')[2]) || 1;
  const [article, setArticle] = useState(null);
  
  useEffect(() => {
    const storedArticle = getArticleDetail(articleId);
    if (storedArticle) {
      setArticle(storedArticle);
    } else if (articleId === 1) {
      setArticle(readerData);
    }
  }, [articleId]);

  if (!article) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>加载文章中...</p>
      </div>
    );
  }

  return <ReadingPageContent article={article} articleId={articleId} />;
}

// ==================== 主应用组件 ====================
function App() {
  return (
    <div className="app-container">
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/article/:id" element={<ReadingPage />} />
          <Route path="/article/:articleId/comprehension" element={<ReadingComprehension />} />
          <Route path="/manager" element={<ArticleManager />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </div>
  );
}

export default App;